import type { HttpResponse } from '../config/ApiClient';

export type SubscriptionCheckoutErrorCode =
  | 'stripe_not_configured'
  | 'stripe_unavailable'
  | 'stripe_checkout_failed'
  | 'catalog_not_synced'
  | 'unknown_product'
  | 'already_owned'
  | 'not_payer'
  | 'assignment_not_found'
  | 'organization_not_found'
  | 'missing_assignment_id'
  | 'invalid_assignment_id'
  | 'missing_session_id'
  | 'session_invalid'
  | 'session_forbidden'
  | 'payment_not_completed'
  | 'app_not_configured'
  | 'unknown';

export interface SubscriptionCheckoutErrorBody {
  error?: string;
  code?: SubscriptionCheckoutErrorCode;
  retryable?: boolean;
  detail?: string;
}

export interface CheckoutErrorDisplay {
  message: string;
  code: SubscriptionCheckoutErrorCode;
  retryable: boolean;
  instruction: string;
}

const INSTRUCTIONS: Record<SubscriptionCheckoutErrorCode, string> = {
  stripe_not_configured:
    'Payments are not set up on this environment yet. Contact your administrator.',
  stripe_unavailable: 'Wait a moment, then use Try again.',
  stripe_checkout_failed: 'Use Try again to restart checkout.',
  catalog_not_synced:
    'The product catalog has not been synced to Stripe. Contact support if this continues.',
  unknown_product: 'Choose a different purchase option or refresh the page.',
  already_owned: 'Refresh the page to see your updated access.',
  not_payer:
    'You must be on this assignment (athlete, coach, parent, payer) or an org admin to purchase for it.',
  assignment_not_found: 'Return to subscriptions or your assignments list and try again.',
  organization_not_found: 'Refresh the page or sign in on the correct organization site.',
  missing_assignment_id: 'Refresh the page and try again.',
  invalid_assignment_id: 'Refresh the page and try again.',
  missing_session_id: 'Start a new purchase from the subscriptions page.',
  session_invalid: 'Start a new purchase from the subscriptions page.',
  session_forbidden: 'Sign in as the account that started this checkout.',
  payment_not_completed: 'Return to subscriptions and start checkout again.',
  app_not_configured: 'Contact support — the app URL is not configured.',
  unknown: 'Try again, or contact support if the problem continues.',
};

function parseCode(value: unknown): SubscriptionCheckoutErrorCode {
  if (typeof value === 'string' && value in INSTRUCTIONS) {
    return value as SubscriptionCheckoutErrorCode;
  }
  return 'unknown';
}

export function parseCheckoutError(response: HttpResponse<unknown>): CheckoutErrorDisplay {
  const body = (response.errors ?? {}) as SubscriptionCheckoutErrorBody;
  const code = parseCode(body.code);
  const message =
  typeof body.error === 'string' && body.error.trim()
    ? body.error
    : typeof response.error === 'string' && response.error.trim()
      ? response.error
      : 'Something went wrong with checkout.';
  const retryable = Boolean(body.retryable);
  return {
    message,
    code,
    retryable,
    instruction: INSTRUCTIONS[code],
  };
}
